import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface NewsletterSubscriptionRequest {
  email: string;
  firstName?: string;
  lastName?: string;
  source?: string;
}

export interface NewsletterSubscriptionResponse {
  success: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NewsletterService {
  // Using environment configuration for MailChimp settings
  private readonly mailchimpUrl = environment.mailchimpUrl || 'https://rentastore.us13.list-manage.com/subscribe/post';
  private readonly mailchimpUserId = environment.mailchimpUserId || 'a696f656183195751e2c27e61';
  private readonly mailchimpListId = environment.mailchimpListId || 'eed9c4afed';
  private readonly mailchimpHiddenField = environment.mailchimpHiddenField || 'b_a696f656183195751e2c27e61_7a1161037d';

  constructor(private http: HttpClient) { }

  /**
   * Subscribe an email address to the newsletter using MailChimp
   * @param request The subscription request containing email and optional details
   * @returns Promise resolving to the subscription response
   */
  async subscribe(request: NewsletterSubscriptionRequest): Promise<NewsletterSubscriptionResponse> {
    try {
      // Create form data to match MailChimp's expected format
      const formData = new FormData();
      formData.append('EMAIL', request.email);
      formData.append(this.mailchimpHiddenField, ''); // Hidden field from environment
      
      // Add first and last name if provided
      if (request.firstName) {
        formData.append('FNAME', request.firstName);
      }
      if (request.lastName) {
        formData.append('LNAME', request.lastName);
      }

      // MailChimp requires form-encoded data for subscriptions
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('EMAIL', request.email);
      urlEncodedData.append(this.mailchimpHiddenField, '');
      
      if (request.firstName) {
        urlEncodedData.append('FNAME', request.firstName);
      }
      if (request.lastName) {
        urlEncodedData.append('LNAME', request.lastName);
      }

      const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      });

      // Build the URL with parameters from environment
      const mailchimpParams = `u=${this.mailchimpUserId}&id=${this.mailchimpListId}`;

      // Make the subscription request to MailChimp
      await this.http.post(
        `${this.mailchimpUrl}?${mailchimpParams}`,
        urlEncodedData.toString(),
        { 
          headers,
          responseType: 'text' // MailChimp returns HTML, not JSON
        }
      ).toPromise();

      // MailChimp doesn't return a clear success/failure in the response
      // So we assume success if no error was thrown
      return { success: true, message: 'Successfully subscribed to newsletter' };

    } catch (error) {
      console.error('Newsletter subscription failed:', error);
      
      // Check if it's a CORS error (common with MailChimp direct calls)
      if (error.status === 0) {
        // CORS error - try alternative approach using JSONP or server proxy
        return this.subscribeViaJsonp(request);
      }
      
      return { 
        success: false, 
        message: 'Failed to subscribe to newsletter. Please try again later.' 
      };
    }
  }

  /**
   * Alternative subscription method using JSONP to avoid CORS issues
   */
  private async subscribeViaJsonp(request: NewsletterSubscriptionRequest): Promise<NewsletterSubscriptionResponse> {
    try {
      // Create JSONP URL for MailChimp using environment values
      const params = new URLSearchParams({
        u: this.mailchimpUserId,
        id: this.mailchimpListId,
        EMAIL: request.email,
        [this.mailchimpHiddenField]: '',
        c: 'JSON_CALLBACK'
      });

      if (request.firstName) {
        params.append('FNAME', request.firstName);
      }
      if (request.lastName) {
        params.append('LNAME', request.lastName);
      }

      // Use the MailChimp JSON endpoint for JSONP
      const jsonpUrl = `${this.mailchimpUrl.replace('/post', '/post-json')}?${params.toString()}`;

      // Use Angular's JSONP client for cross-origin requests
      const response = await this.http.jsonp(jsonpUrl, 'c').toPromise() as any;

      if (response && response.result === 'success') {
        return { success: true, message: 'Successfully subscribed to newsletter' };
      } else {
        return { 
          success: false, 
          message: response?.msg || 'Failed to subscribe to newsletter' 
        };
      }

    } catch (error) {
      console.error('JSONP newsletter subscription failed:', error);
      return { 
        success: false, 
        message: 'Failed to subscribe to newsletter. Please try again later.' 
      };
    }
  }

  /**
   * Unsubscribe an email address from the newsletter
   * Note: MailChimp doesn't provide a simple unsubscribe API endpoint
   * Users should use the unsubscribe link in emails they receive
   */
  async unsubscribe(email: string): Promise<NewsletterSubscriptionResponse> {
    console.warn('Unsubscribe functionality not available through MailChimp API. Users should use email unsubscribe links.');
    return { 
      success: false, 
      message: 'Please use the unsubscribe link in our newsletter emails to unsubscribe.' 
    };
  }
}